import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  listDomains, addDomain, removeDomain, verifyDomain,
  getDomainStatus, setPrimaryDomain, setDomainRedirect,
  getManagedSubdomain, retryDomain, authFetch,
} from '../services/api';
import { useToast } from '../hooks/useToast';

const POLL_INTERVAL_MS = 60_000;

// ── Managed Subdomain Banner ──────────────────────────────────────────────────

function ManagedSubdomainBanner({ subdomain, url }) {
  const toast = useToast();
  if (!subdomain) return null;
  return (
    <div style={{
      padding: '14px 16px', marginBottom: 20,
      background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)',
      borderRadius: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(99,102,241,0.2)', color: '#818cf8' }}>DS CodeXAI</span>
            <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>🔒 HTTPS Active</span>
          </div>
          <a href={url} target="_blank" rel="noreferrer"
            style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600, color: '#818cf8', textDecoration: 'none' }}>
            {url}
          </a>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Your permanent managed URL — always points to this deployment.
          </p>
        </div>
        <button
          onClick={() => { navigator.clipboard.writeText(url); toast.success('Copied!'); }}
          style={s.ghostBtn}
        >
          Copy URL
        </button>
      </div>
    </div>
  );
}

// ── State machine status display ─────────────────────────────────────────────

const DOMAIN_STATE_CONFIG = {
  PENDING:                  { color: '#64748b', bg: 'rgba(100,116,139,0.12)', label: '◷ Pending',              step: 0 },
  PROVIDER_DOMAIN_CREATED:  { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  label: '✓ Domain Registered',   step: 1 },
  DNS_PENDING:              { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  label: '🔍 Checking DNS',        step: 2 },
  DNS_VERIFIED:             { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  label: '✓ DNS Verified',         step: 3 },
  PROVIDER_ATTACHING:       { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', label: '⟳ Attaching Domain',     step: 4 },
  PROVIDER_CONFIGURING:     { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', label: '⟳ Configuring',          step: 5 },
  SSL_PENDING:              { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  label: '🔒 Provisioning SSL',    step: 6 },
  ACTIVE:                   { color: '#10b981', bg: 'rgba(16,185,129,0.12)',  label: '✓ Active',               step: 7 },
  ERROR:                    { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   label: '✕ Error',                step: -1 },
};

const PIPELINE_STEPS = [
  { key: 'PROVIDER_DOMAIN_CREATED', label: 'Registered' },
  { key: 'DNS_PENDING',             label: 'DNS Check' },
  { key: 'DNS_VERIFIED',            label: 'DNS Verified' },
  { key: 'PROVIDER_ATTACHING',      label: 'Attaching' },
  { key: 'PROVIDER_CONFIGURING',    label: 'Configuring' },
  { key: 'SSL_PENDING',             label: 'SSL' },
  { key: 'ACTIVE',                  label: 'Active' },
];

function DomainStateBadge({ domainStatus }) {
  const cfg = DOMAIN_STATE_CONFIG[domainStatus] || DOMAIN_STATE_CONFIG.PENDING;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function PipelineProgress({ domainStatus }) {
  const currentStep = (DOMAIN_STATE_CONFIG[domainStatus] || DOMAIN_STATE_CONFIG.PENDING).step;
  const isError = domainStatus === 'ERROR';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 10, flexWrap: 'nowrap', overflowX: 'auto' }}>
      {PIPELINE_STEPS.map((step, i) => {
        const stepCfg = DOMAIN_STATE_CONFIG[step.key];
        const done = !isError && currentStep > stepCfg.step;
        const active = !isError && currentStep === stepCfg.step;
        const color = isError ? '#ef4444' : done ? '#10b981' : active ? stepCfg.color : '#334155';
        return (
          <React.Fragment key={step.key}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56 }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 10, fontWeight: 700,
                background: done ? '#10b981' : active ? color : 'var(--bg-secondary)',
                color: done || active ? '#fff' : '#64748b',
                border: `2px solid ${done ? '#10b981' : active ? color : '#334155'}`,
                flexShrink: 0,
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 9, color: done ? '#10b981' : active ? color : '#64748b', marginTop: 3, textAlign: 'center', lineHeight: 1.2 }}>
                {step.label}
              </span>
            </div>
            {i < PIPELINE_STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? '#10b981' : '#334155', minWidth: 8, marginBottom: 14 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── DNS Records table ─────────────────────────────────────────────────────────

function DNSRecordsTable({ records, onClose }) {
  const toast = useToast();
  if (!records || records.length === 0) return null;
  return (
    <div style={s.dnsOverlay} onClick={onClose}>
      <div style={s.dnsModal} onClick={e => e.stopPropagation()}>
        <div style={s.dnsHeader}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>DNS Records</span>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          Add these records to your DNS provider to verify your domain.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Type', 'Host', 'Value', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={s.dnsCell}>
                  <span style={{ ...s.typeTag, background: r.type === 'A' ? 'rgba(99,102,241,0.15)' : 'rgba(16,185,129,0.12)', color: r.type === 'A' ? '#818cf8' : '#10b981' }}>
                    {r.type}
                  </span>
                </td>
                <td style={{ ...s.dnsCell, fontFamily: 'monospace' }}>{r.host}</td>
                <td style={{ ...s.dnsCell, fontFamily: 'monospace', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.value}</td>
                <td style={s.dnsCell}>
                  <button
                    onClick={() => { navigator.clipboard.writeText(r.value); toast.success('Copied!'); }}
                    style={s.copyBtn}
                  >
                    Copy
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── DNS Setup Instructions (shown when pending/unknown) ──────────────────────

function DNSInstructions({ jobId, domainId }) {
  const toast = useToast();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!jobId || !domainId) return;
    authFetch(`/api/deployments/${jobId}/domains/${domainId}/dns-instructions`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setConfig(data); })
      .catch(() => {});
  }, [jobId, domainId]);

  if (!config) return (
    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>Loading DNS instructions…</div>
  );

  // ── Configuration error (e.g. PROXY_IP not set in production) ────────────
  if (config.error) return (
    <div style={s.dnsBox}>
      <p style={{ fontWeight: 600, color: '#f59e0b', margin: '0 0 6px' }}>⚠ DNS not configured</p>
      <p style={{ color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>{config.error}</p>
    </div>
  );

  // ── Not yet deployed ─────────────────────────────────────────────────────
  if (config.is_local) return (
    <div style={s.dnsBox}>
      <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>
        🚀 Deploy first to get DNS instructions
      </p>
      <p style={{ color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.6, fontSize: 12 }}>
        This project has not been deployed yet. Deploy it first, then come back
        here to connect your custom domain.
      </p>
      {config.local_hosts?.length > 0 && (
        <>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px', fontSize: 12 }}>
            Local testing only — add to your hosts file:
          </p>
          <div style={{
            background: 'var(--bg-secondary, rgba(0,0,0,0.2))', borderRadius: 6,
            padding: '10px 12px', fontFamily: 'monospace', fontSize: 12,
            color: 'var(--text-primary)', marginBottom: 10,
          }}>
            {config.local_hosts.map((line, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{line}</span>
                <button
                  onClick={() => { navigator.clipboard.writeText(line); toast.success('Copied!'); }}
                  style={{ ...s.copyBtn, marginLeft: 12 }}
                >Copy</button>
              </div>
            ))}
          </div>
          <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: 11, lineHeight: 1.5 }}>
            Hosts file: <code>/etc/hosts</code> (macOS/Linux) or
            <code> C:\Windows\System32\drivers\etc\hosts</code> (Windows).
          </p>
        </>
      )}
    </div>
  );

  // ── Production: valid DNS records ─────────────────────────────────────────
  return (
    <div style={s.dnsBox}>
      <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 10px' }}>
        📋 Add these DNS records at your domain registrar:
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Type', 'Host', 'Value'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 11 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {config.records.map((r, i) => (
            <tr key={i}>
              <td style={{ padding: '4px 8px' }}>
                <span style={{ ...s.typeTag, background: r.type === 'A' ? 'rgba(99,102,241,0.15)' : 'rgba(16,185,129,0.12)', color: r.type === 'A' ? '#818cf8' : '#10b981' }}>{r.type}</span>
              </td>
              <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: 'var(--text-primary)' }}>{r.host}</td>
              <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                {r.value}
                <button onClick={() => { navigator.clipboard.writeText(r.value); toast.success('Copied!'); }} style={{ ...s.copyBtn, marginLeft: 8 }}>Copy</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5, fontSize: 11 }}>
        DNS changes can take up to 24 hours. The platform checks automatically every minute.
      </p>
    </div>
  );
}


function AddDomainModal({ jobId, onAdded, onClose }) {
  const [domain, setDomain] = useState('');
  const [redirect, setRedirect] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = domain.trim().toLowerCase();
    if (!trimmed) { setError('Domain is required'); return; }
    setError('');
    setLoading(true);
    try {
      const result = await addDomain(jobId, trimmed, redirect || null);
      toast.success(`Domain ${trimmed} added successfully`);
      onAdded(result);
    } catch (err) {
      setError(err.message || 'Failed to add domain');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.dnsOverlay} onClick={onClose}>
      <div style={{ ...s.dnsModal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div style={s.dnsHeader}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Add Custom Domain</span>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <label style={s.label}>Domain</label>
          <input
            ref={inputRef}
            value={domain}
            onChange={e => setDomain(e.target.value)}
            placeholder="example.com or www.example.com"
            style={{ ...s.input, borderColor: error ? '#ef4444' : 'var(--border)' }}
            disabled={loading}
          />
          {error && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{error}</p>}

          <label style={{ ...s.label, marginTop: 14 }}>Redirect (optional)</label>
          <select value={redirect} onChange={e => setRedirect(e.target.value)} style={s.input} disabled={loading}>
            <option value="">No redirect</option>
            <option value="www_to_root">www → root (example.com)</option>
            <option value="root_to_www">root → www (www.example.com)</option>
          </select>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="submit" disabled={loading} style={s.primaryBtn}>
              {loading ? 'Adding…' : 'Add Domain'}
            </button>
            <button type="button" onClick={onClose} style={s.ghostBtn}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Remove Confirmation Dialog ────────────────────────────────────────────────

function RemoveDomainDialog({ domain, onConfirm, onClose, loading }) {
  return (
    <div style={s.dnsOverlay} onClick={onClose}>
      <div style={{ ...s.dnsModal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div style={s.dnsHeader}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Remove Domain</span>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '12px 0 20px' }}>
          Are you sure you want to remove <strong style={{ color: 'var(--text-primary)' }}>{domain}</strong>?
          This will detach it from your deployment.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onConfirm} disabled={loading} style={{ ...s.primaryBtn, background: '#ef4444' }}>
            {loading ? 'Removing…' : 'Remove Domain'}
          </button>
          <button onClick={onClose} style={s.ghostBtn}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Domain Card ───────────────────────────────────────────────────────────────

function DomainCard({ domain, jobId, onRefresh, onRemoveClick }) {
  const [busy, setBusy] = useState(false);
  const [showDns, setShowDns] = useState(false);
  const toast = useToast();

  const domainStatus = domain.domain_status || 'PENDING';
  const isActive = domainStatus === 'ACTIVE';
  const isError = domainStatus === 'ERROR';
  const showDnsInstructions = ['PENDING', 'PROVIDER_DOMAIN_CREATED', 'DNS_PENDING'].includes(domainStatus);

  const handleRefresh = async () => {
    setBusy(true);
    try {
      await getDomainStatus(jobId, domain.id);
      await onRefresh();
      toast.info('Status refreshed');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const handleRetry = async () => {
    setBusy(true);
    try {
      await retryDomain(jobId, domain.id);
      await onRefresh();
      toast.success('Retrying domain verification…');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const handleSetPrimary = async () => {
    setBusy(true);
    try {
      await setPrimaryDomain(jobId, domain.id);
      await onRefresh();
      toast.success(`${domain.domain} set as primary`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      {showDns && (
        <DNSRecordsTable records={domain.dns_records} onClose={() => setShowDns(false)} />
      )}
      <div style={{ ...s.card, borderColor: isActive ? 'rgba(16,185,129,0.3)' : isError ? 'rgba(239,68,68,0.3)' : 'var(--border)' }}>
        {/* Header row */}
        <div style={s.cardTop}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', fontFamily: 'monospace' }}>
              {isActive
                ? <a href={`https://${domain.domain}`} target="_blank" rel="noreferrer"
                    style={{ color: '#10b981', textDecoration: 'none' }}>{domain.domain} ↗</a>
                : domain.domain
              }
            </span>
            {domain.is_primary && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>PRIMARY</span>
            )}
            {domain.redirect_mode && (
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                {domain.redirect_mode === 'www_to_root' ? 'www → root' : 'root → www'}
              </span>
            )}
            <DomainStateBadge domainStatus={domainStatus} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={handleRefresh} disabled={busy} style={s.iconBtn} title="Refresh">{busy ? '⟳' : '↻'}</button>
            {domain.dns_records?.length > 0 && (
              <button onClick={() => setShowDns(true)} style={s.iconBtn} title="View DNS records">DNS</button>
            )}
            {!domain.is_primary && isActive && (
              <button onClick={handleSetPrimary} disabled={busy} style={s.iconBtn} title="Set as primary">★</button>
            )}
            {isError && (
              <button onClick={handleRetry} disabled={busy} style={{ ...s.iconBtn, color: '#f59e0b' }} title="Retry">↺ Retry</button>
            )}
            <button onClick={() => onRemoveClick(domain)} style={{ ...s.iconBtn, color: '#ef4444' }} title="Remove">✕</button>
          </div>
        </div>

        {/* Pipeline progress bar */}
        <PipelineProgress domainStatus={domainStatus} />

        {/* Error message */}
        {isError && domain.verification_error && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, fontSize: 12, color: '#ef4444' }}>
            <strong>Error:</strong> {domain.verification_error}
          </div>
        )}

        {/* DNS instructions — only shown while waiting for DNS propagation */}
        {showDnsInstructions && (
          <DNSInstructions jobId={jobId} domainId={domain.id} />
        )}

        {/* Active confirmation */}
        {isActive && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 6, fontSize: 12, color: '#10b981' }}>
            🔒 HTTPS active — your domain is live and serving this deployment.
          </div>
        )}

        {domain.last_checked_at && (
          <p style={{ fontSize: 10, color: 'var(--text-muted)', margin: '8px 0 0' }}>
            Last checked: {new Date(domain.last_checked_at).toLocaleString()}
          </p>
        )}
      </div>
    </>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────

function DomainSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {[1, 2].map(i => (
        <div key={i} style={{ ...s.card, animation: 'pulse 1.5s ease-in-out infinite' }}>
          <div style={{ height: 14, width: '40%', background: 'var(--border)', borderRadius: 4 }} />
          <div style={{ height: 10, width: '60%', background: 'var(--border)', borderRadius: 4, marginTop: 10 }} />
        </div>
      ))}
    </div>
  );
}

// ── Main DomainsTab ───────────────────────────────────────────────────────────

export default function DomainsTab({ jobId, deployUrl, managedSubdomain: managedSubdomainProp }) {
  const [domains, setDomains] = useState([]);
  const [managedSubdomain, setManagedSubdomain] = useState(
    managedSubdomainProp ? { subdomain: managedSubdomainProp, url: `https://${managedSubdomainProp}` } : null
  );
  // null = still loading from DB, object = loaded
  const [deploymentInfo, setDeploymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const toast = useToast();
  const pollRef = useRef(null);

  // Authoritative deployment detection — always queries the DB.
  // Never uses backend environment, localhost detection, or frontend state.
  useEffect(() => {
    if (!jobId) return;
    authFetch(`/api/deployments/${jobId}/deployment-info`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setDeploymentInfo(data || { deployment_found: false, deployment_status: 'NOT_DEPLOYED', domain_flow: 'LOCAL' });
        if (data && data.managed_subdomain) {
          setManagedSubdomain({ subdomain: data.managed_subdomain, url: data.managed_url });
        }
      })
      .catch(() => {
        setDeploymentInfo({ deployment_found: false, deployment_status: 'NOT_DEPLOYED', domain_flow: 'LOCAL' });
      });
  }, [jobId]);

  const fetchDomains = useCallback(async () => {
    try {
      const [data, managed] = await Promise.all([
        listDomains(jobId),
        getManagedSubdomain(jobId).catch(() => null),
      ]);
      const custom = data.filter(d => d.domain_type !== 'managed');
      setDomains(custom);
      if (managed) setManagedSubdomain(managed);

      // Poll faster (15 s) while any domain is mid-pipeline
      const inProgress = custom.some(d =>
        !['ACTIVE', 'ERROR', 'PENDING'].includes(d.domain_status || 'PENDING')
      );
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchDomains, inProgress ? 15_000 : POLL_INTERVAL_MS);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchDomains();
    pollRef.current = setInterval(fetchDomains, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchDomains]);

  const handleAdded = (newDomain) => {
    setShowAdd(false);
    setDomains(prev => [...prev, newDomain]);
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await removeDomain(jobId, removeTarget.id);
      setDomains(prev => prev.filter(d => d.id !== removeTarget.id));
      toast.success(`${removeTarget.domain} removed`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRemoving(false);
      setRemoveTarget(null);
    }
  };

  const isDeployed = deploymentInfo && deploymentInfo.deployment_found === true;

  return (
    <div style={s.container}>
      {showAdd && (
        <AddDomainModal jobId={jobId} onAdded={handleAdded} onClose={() => setShowAdd(false)} />
      )}
      {removeTarget && (
        <RemoveDomainDialog
          domain={removeTarget.domain}
          onConfirm={handleRemoveConfirm}
          onClose={() => setRemoveTarget(null)}
          loading={removing}
        />
      )}

      {/* Managed subdomain banner */}
      <ManagedSubdomainBanner
        subdomain={managedSubdomain?.subdomain}
        url={managedSubdomain?.url}
      />

      {/* Deployment status row — shown once DB query returns */}
      {deploymentInfo && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
          padding: '10px 14px', borderRadius: 8, fontSize: 12,
          background: isDeployed ? 'rgba(16,185,129,0.07)' : 'rgba(100,116,139,0.07)',
          border: `1px solid ${isDeployed ? 'rgba(16,185,129,0.2)' : 'rgba(100,116,139,0.15)'}`,
        }}>
          <span style={{ fontWeight: 700, color: isDeployed ? '#10b981' : '#64748b' }}>
            {isDeployed ? '✓ Deployed' : '◷ Not deployed'}
          </span>
          {isDeployed && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>·</span>
              <span style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>{deploymentInfo.provider}</span>
              <span style={{ color: 'var(--text-muted)' }}>·</span>
              <a href={deploymentInfo.deployment_url} target="_blank" rel="noreferrer"
                style={{ color: '#818cf8', fontFamily: 'monospace', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                {deploymentInfo.deployment_url}
              </a>
            </>
          )}
        </div>
      )}

      {/* Not-deployed gate — only when DB confirms no deployment */}
      {deploymentInfo && !isDeployed && (
        <div style={{ ...s.empty, marginBottom: 20 }}>
          <span style={{ fontSize: 32 }}>🚀</span>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '8px 0 4px' }}>Deploy first</p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
            Publish your project to get DNS instructions and connect a custom domain.
          </p>
        </div>
      )}

      {/* Everything below only shown when deployed */}
      {isDeployed && (
        <>
          <div style={s.header}>
            <div>
              <h3 style={s.title}>Custom Domains</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
                Attach your own domain — it will serve the same deployment.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={fetchDomains} style={s.ghostBtn} title="Refresh all">↻ Refresh</button>
              <button onClick={() => setShowAdd(true)} style={s.primaryBtn}>+ Add Domain</button>
            </div>
          </div>

          {loading ? (
            <DomainSkeleton />
          ) : domains.length === 0 ? (
            <div style={s.empty}>
              <span style={{ fontSize: 32 }}>🌐</span>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '8px 0 4px' }}>No custom domains</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                Add a custom domain to point your own URL to this deployment.
              </p>
              <button onClick={() => setShowAdd(true)} style={{ ...s.primaryBtn, marginTop: 16 }}>
                + Add Domain
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {domains.map(d => (
                <DomainCard
                  key={d.id}
                  domain={d}
                  jobId={jobId}
                  onRefresh={fetchDomains}
                  onRemoveClick={setRemoveTarget}
                />
              ))}
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
      `}</style>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  container: { padding: '20px 24px', maxWidth: 680 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  card: {
    background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '14px 16px',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '48px 24px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 10,
  },
  dnsOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  dnsModal: {
    background: 'var(--bg-card)', borderRadius: 12, padding: '24px',
    width: '90%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    border: '1px solid var(--border)',
  },
  dnsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  dnsCell: { padding: '8px 8px', color: 'var(--text-primary)', verticalAlign: 'middle' },
  typeTag: { fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4 },
  copyBtn: {
    padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
    background: 'var(--bg-input, rgba(99,102,241,0.1))', border: '1px solid var(--border)',
    borderRadius: 5, color: 'var(--text-primary)',
  },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 },
  input: {
    width: '100%', padding: '9px 12px', background: 'var(--bg-input, var(--bg-secondary))',
    border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-primary)',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  },
  primaryBtn: {
    padding: '8px 18px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  ghostBtn: {
    padding: '8px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 7, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer',
  },
  iconBtn: {
    padding: '4px 10px', background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 6, color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
    fontWeight: 600,
  },
  closeBtn: {
    background: 'none', border: 'none', color: 'var(--text-muted)',
    fontSize: 16, cursor: 'pointer', padding: '2px 6px',
  },
  dnsBox: {
    marginTop: 12, padding: '12px 14px', background: 'rgba(99,102,241,0.06)',
    border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, fontSize: 12,
  },
};
